import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import { useSnackbar } from 'notistack';

import { signFile } from '../../../services/fileSignaturesService';

const UserFileSignDialog = ({ open, onClose, file, t, onSigned }) => {
  const { enqueueSnackbar } = useSnackbar();

  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) return;

    setSubmitting(true);
    setError(null);

    try {
      const res = await signFile({
        contentType: 'accounts.userfile',
        objectId: file.id,
        code,
      });

      if (res.success) {
        enqueueSnackbar(t('files.sign_success', 'Súbor bol podpísaný.'), {
          variant: 'success',
        });

        if (onSigned) {
          await onSigned(file.id);
        }

        setCode('');
        onClose();
      } else {
        const msg =
          res.error || t('files.sign_failed', 'Nepodarilo sa podpísať súbor.');
        setError(msg);
        enqueueSnackbar(msg, { variant: 'error' });
      }
    } catch (err) {
      console.error('Sign error', err);
      const msg = t('files.sign_failed', 'Nepodarilo sa podpísať súbor.');
      setError(msg);
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (submitting) return;
    setError(null);
    setCode('');
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: { 
          borderRadius: 3, 
          boxShadow: 8 
        }
      }}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'background.paper', py: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('files.sign_dialog_title', 'Podpísať súbor')}
          </Typography>
          <IconButton 
            onClick={handleClose} 
            size="small"
            disabled={submitting}
            sx={{ bgcolor: 'action.hover', '&:hover': { bgcolor: 'action.selected' } }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: { xs: 2, md: 3 } }}>
          {file && (
            <Box sx={(theme) => ({ 
                mb: 3, 
                p: 2, 
                bgcolor: alpha(theme.palette.primary.main, 0.1), 
                borderRadius: 2, 
                border: '1px solid', 
                borderColor: alpha(theme.palette.primary.main, 0.2) 
            })}>
              <Typography 
                variant="subtitle2" 
                color="primary.main" 
                sx={{ textTransform: 'uppercase', fontSize: 11, letterSpacing: 0.5, fontWeight: 600 }}
              >
                {t('files.signing_file', 'Podpisujete súbor:')}
              </Typography>
              <Typography 
                variant="body2" 
                color="text.primary" 
                sx={{ fontWeight: 500, mt: 0.5, wordBreak: 'break-all' }}
              >
                {file.filename}
              </Typography>
            </Box>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t(
              'files.sign_dialog_description',
              'Zadajte 6-miestny kód z aplikácie Google Authenticator na potvrdenie podpisu.'
            )}
          </Typography>

          <TextField
            label={t('files.sign_code_label', 'Overovací kód')}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            fullWidth
            size="medium"
            autoFocus
            sx={{ 
              '& .MuiOutlinedInput-root': { borderRadius: 2 } 
            }}
            inputProps={{
              inputMode: 'numeric',
              pattern: '[0-9]*',
              maxLength: 6,
              style: { letterSpacing: '0.25em', textAlign: 'center', fontSize: '1.2rem', fontWeight: 600 }
            }}
          />

          {error && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, bgcolor: 'background.paper' }}>
          <Button 
            onClick={handleClose} 
            disabled={submitting}
            sx={{ fontWeight: 600, color: 'text.secondary' }}
          >
            {t('common.cancel', 'Zrušiť')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={submitting || !code}
            disableElevation
            sx={{ borderRadius: 2, fontWeight: 600, px: 3 }}
          >
            {submitting ? (
              <CircularProgress size={22} color="inherit" />
            ) : (
              t('files.sign_confirm', 'Podpísať')
            )}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default UserFileSignDialog;